import { FeatureFlags } from "@/constants/featureFlags";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { DynamicSupplyHardware } from "@/types";
import {
  getOriginalAndDiscountedPriceFromComparison,
  useCompetitorPriceMap
} from "../priceComparison";
import { getFlashSaleGpuPrice, isFlashSaleGpu } from "@/utils/cluster";
import { useMemo } from "react";

const getOriginalAndDiscountedPrice = (
  hardwareName: string,
  hardwareId: string,
  deploymentId: string | undefined,
  oldPrice: number,
  price: number,
  isPriceComparisonEnabled: boolean,
  competitorPriceMap?: Record<string, number>
): { originalPrice: number; discount: number } => {
  if (isPriceComparisonEnabled) {
    return getOriginalAndDiscountedPriceFromComparison(
      {
        hardwareIdentifier: hardwareName,
        hardwareId,
        deploymentId
      },
      price,
      oldPrice,
      competitorPriceMap
    );
  } else {
    const isOnSale = isFlashSaleGpu(hardwareName);
    const flashSaleOriginalPrice = isOnSale
      ? Number(getFlashSaleGpuPrice(hardwareName) ?? 0)
      : undefined;
    const originalPrice =
      flashSaleOriginalPrice && !Number.isNaN(flashSaleOriginalPrice) && flashSaleOriginalPrice > 0
        ? flashSaleOriginalPrice
        : oldPrice;
    const discount =
      originalPrice && originalPrice > price
        ? Math.round(((originalPrice - price) / originalPrice) * 100)
        : 0;

    return { originalPrice, discount };
  }
};

export const useHardwareDiscount = (item: DynamicSupplyHardware) => {
  const priceComparisonVariant = useFeatureFlag(FeatureFlags.PRICE_COMPARISON);
  const isPriceComparisonEnabled =
    priceComparisonVariant && priceComparisonVariant.value !== "control" ? true : false;
  const competitorPriceMap = useCompetitorPriceMap();
  const { originalPrice, discount } = useMemo(
    () =>
      getOriginalAndDiscountedPrice(
        item.hardwareName,
        item.hardwareId,
        item.deploymentId,
        item.oldPrice!,
        item.price,
        isPriceComparisonEnabled,
        competitorPriceMap
      ),
    [
      item.hardwareName,
      item.hardwareId,
      item.deploymentId,
      item.oldPrice,
      item.price,
      isPriceComparisonEnabled,
      competitorPriceMap
    ]
  );
  const hasDiscount = Number.isFinite(originalPrice) && discount > 0;

  return { originalPrice, discount, hasDiscount, isPriceComparisonEnabled };
};
